import { ChevronRight } from "@/components/ui/icons";
import { Pressable, Text, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { PICKS_IN_LINE_MESSAGE } from "@/hooks/usePullSheet";
import { formatMoney } from "@/lib/money/money";
import { useThemeColors } from "@/theme/theme";

export type PullSheetEventPhase = "upcoming" | "live";

export const pullSheetNoHoldWarning = (phase: PullSheetEventPhase) =>
  phase === "upcoming"
    ? "Nothing is held. Vendors keep selling from this stock until doors open."
    : "Cards aren't held — first in line gets them.";

/**
 * Returns the bar's caption once the list has been sent (TRO-673).
 *
 * It shows the customer's live place in line when the server knows it, or "You're in line" when it
 * does not (a sheet that left the queue between reads has no position), and in both cases the
 * reason the pick controls are locked.
 *
 * This is the only visible explanation on the storefront: the locked controls carry theirs in their
 * accessible name, which a sighted customer never hears. The bar is the one element on every browse
 * surface while waiting, so the reason goes here. It replaces the no-hold warning.
 */
export const pullSheetInLineCaption = (position: number | null, total: number | null) =>
  position != null && total != null
    ? `#${position} of ${total} in line — ${PICKS_IN_LINE_MESSAGE}.`
    : `You're in line — ${PICKS_IN_LINE_MESSAGE}.`;

type Props = {
  count: number;
  subtotal: string;
  onPress: () => void;
  phase?: PullSheetEventPhase;
  /**
   * The list is with the Vendor (`presented`). The bar stops being a "review and edit" entry point
   * and becomes the customer's way back to their place in line, the only route to that screen from
   * the browse list. It must return to the stored sheet, not fork a second one (TRO-673).
   */
  inLine?: boolean;
  /** Live queue position and length while `inLine` (#464); both null until the first poll. */
  queuePosition?: number | null;
  queueTotal?: number | null;
};

/**
 * A sticky bottom bar showing the Pull Sheet's running count and subtotal while browsing (#342).
 *
 * The trailing label and chevron in the Signal accent make it read as tappable: it is the only path
 * to the review drawer and Send list. Renders nothing while the sheet is empty.
 */
export function PullSheetBar(
  {
    count,
    subtotal,
    onPress,
    phase = "live",
    inLine = false,
    queuePosition = null,
    queueTotal = null,
  }: Props,
) {
  const insets = useSafeAreaInsets();
  const c = useThemeColors();
  if (count === 0) return null;
  const caption = inLine
    ? pullSheetInLineCaption(queuePosition, queueTotal)
    : pullSheetNoHoldWarning(phase);

  return (
    <Pressable
      testID="pull-sheet-bar"
      onPress={onPress}
      accessibilityRole="button"
      // Both states open the same drawer but promise different things: an editable list or the
      // waiting screen. Naming the destination keeps the queue view findable after check-out.
      accessibilityLabel={inLine ? "See your place in line" : "Review your list"}
      accessibilityHint={caption}
      className="absolute inset-x-0 bottom-0 border-t border-border bg-bg px-4 py-3"
      style={{ paddingBottom: insets.bottom + 12 }}
    >
      <View className="flex-row items-center justify-between">
        <Text
          testID="pull-sheet-bar-count"
          className="text-base font-title text-foreground"
        >
          {count} {count === 1 ? "item" : "items"} · {formatMoney(subtotal)}
        </Text>
        <View testID="pull-sheet-bar-review" className="flex-row items-center gap-1">
          <Text className="text-base font-label text-primary">
            {inLine ? "In line" : "Review"}
          </Text>
          <ChevronRight size={18} color={c.primary} />
        </View>
      </View>
      {
        /* One testID for this line in both states (ADR-0021: testIDs are semantic and stable).
         It is always the bar's caption, whether it shows the no-hold warning or the place in line. */
      }
      <Text
        testID="pull-sheet-bar-caption"
        className={!inLine && phase === "upcoming"
          ? "mt-1 text-xs font-body text-warning"
          : "mt-1 text-xs font-body text-muted-foreground"}
      >
        {caption}
      </Text>
    </Pressable>
  );
}
